import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FadeIn } from "@/components/animated";
import { categoriesApi } from "@/features/categories/api/categoriesApi";
import { Card, CardContent, Badge, Button, Skeleton } from "@learn-me/ui";
import { formatCurrency } from "@learn-me/utils";
import { normalizeImageUrl } from "@/lib/imageUrl";
import {
  BookOpen,
  Briefcase,
  Trophy,
  ArrowRight,
  Clock,
  Users,
  GraduationCap,
} from "lucide-react";

// Iconos para cada área
const areaIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  "cursos-libres": BookOpen,
  "cursos-profesionales-tecnicos": Briefcase,
  "cursos-empresariales-liderazgo": Trophy,
};

export function TrainingAreasSection() {
  const [activeTab, setActiveTab] = useState(0);

  const { data: mainAreas, isLoading } = useQuery({
    queryKey: ["main-areas"],
    queryFn: () => categoriesApi.getMainAreas(),
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-secondary-50">
        <div className="container">
          <div className="text-center mb-12">
            <Skeleton className="h-8 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="flex justify-center gap-4 mb-12">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-48" />
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!mainAreas || mainAreas.length === 0) {
    return null;
  }

  const activeArea = mainAreas[activeTab];

  return (
    <section className="py-20 bg-secondary-50">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
              Nuestras Áreas de Formación
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Elige tu camino de aprendizaje
            </h2>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              Ofrecemos capacitación especializada en tres áreas principales
              para impulsar tu desarrollo personal y profesional
            </p>
          </div>
        </FadeIn>

        {/* Tabs */}
        <FadeIn delay={0.2}>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {mainAreas.map((area, index) => {
              const Icon = areaIcons[area.slug] || BookOpen;
              const isActive = activeTab === index;

              return (
                <button
                  key={area.id}
                  onClick={() => setActiveTab(index)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    isActive
                      ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                      : "bg-white text-secondary-700 hover:bg-primary-50 hover:text-primary-700 border border-secondary-200"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{area.name}</span>
                  <span className="sm:hidden">{area.name.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </FadeIn>

        {/* Contenido del área activa */}
        <AnimatePresence mode="wait">
          {activeArea && (
            <motion.div
              key={activeArea.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Descripción del área */}
              <div className="text-center mb-8">
                <p className="text-secondary-600 max-w-3xl mx-auto">
                  {activeArea.description}
                </p>
                <p className="text-sm text-secondary-500 mt-2">
                  {activeArea.coursesCount} cursos disponibles
                </p>
              </div>

              {/* Grid de cursos destacados */}
              {activeArea.featuredCourses.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {activeArea.featuredCourses.map((course) => (
                    <Link key={course.id} to={`/courses/${course.slug}`}>
                      <Card className="h-full group hover:shadow-lg transition-all duration-300 overflow-hidden">
                        <div className="aspect-video bg-secondary-100 relative overflow-hidden">
                          {course.thumbnailUrl ? (
                            <img
                              src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                              alt={course.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <GraduationCap className="h-12 w-12 text-secondary-300" />
                            </div>
                          )}
                          {course.isFree && (
                            <Badge className="absolute top-2 right-2 bg-green-500">
                              Gratis
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-secondary-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                            {course.title}
                          </h3>
                          {course.shortDescription && (
                            <p className="text-sm text-secondary-500 line-clamp-2 mb-3">
                              {course.shortDescription}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-3 text-secondary-500">
                              {course.duration && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {Math.round(course.duration / 60)}h
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {course.studentsCount}
                              </span>
                            </div>
                            {!course.isFree && course.salePrice && (
                              <span className="font-semibold text-primary-600">
                                {formatCurrency(
                                  course.salePrice,
                                  course.currency,
                                )}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl">
                  <GraduationCap className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                  <p className="text-secondary-500">
                    Próximamente agregaremos cursos a esta área
                  </p>
                </div>
              )}

              {/* Botón ver todos */}
              <div className="text-center mt-8">
                <Link to={`/courses?category=${activeArea.slug}`}>
                  <Button variant="outline" size="lg">
                    Ver todos los cursos de {activeArea.name}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
